/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';

import { TabView, TabPanel } from 'primereact/tabview';
import { InputSwitch } from 'primereact/inputswitch';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import FormDatePicker from '../../components/forms/form-date-picker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { PERMISSIONS } from '../../constants/permissions';

import { projectsService } from '../../services/projects.service';
import { partnersService } from '../../services/partners.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { ProjectSettingsTab } from './project-settings-tab';
import ScreenHeader from '../../components/common/screen-header';

const projectSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Must be ≤ 120 characters').trim(),
  appCode: z.string().min(1, 'Project code is required').max(50, 'Must be ≤ 50 characters').regex(/^[a-zA-Z0-9_-]+$/, 'Only letters, digits, hyphens and underscores are allowed').trim(),
  description: z.string().max(500, 'Must be ≤ 500 characters').trim().optional().or(z.literal('')),
  imageUrl: z
    .string()
    .max(2048, 'Must be ≤ 2048 characters')
    .trim()
    .regex(/^(https?:\/\/|\/)/, 'Must be an http(s) URL or a root-relative path')
    .optional()
    .or(z.literal('')),
  partnerMasterId: z.string().trim().optional().or(z.literal('')),
  startAt: z.string().optional().or(z.literal('')),
  paymentDone: z.boolean().optional(),
});

export type ProjectFormData = z.infer<typeof projectSchema>;

const ProjectForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'apps',
    basePath: '/projects',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema) as Resolver<ProjectFormData>,
    defaultValues: {
      name: '',
      appCode: '',
      description: '',
      imageUrl: '',
      partnerMasterId: '',
      startAt: '',
      paymentDone: false,
    },
    mode: 'onChange',
  });

  // Partners the caller may access (project-scoped, cross-project for admins). Optional:
  // fails silently (empty options) when the user lacks PARTNER.READ — the field is
  // non-blocking and only affects partner-wide scoping when set.
  const { data: partners = [] } = useQuery({
    queryKey: ['partners', 'dropdown'],
    queryFn: async () => {
      try { return await partnersService.getAllForDropdown(); }
      catch { return []; }
    },
    staleTime: 5 * 60 * 1000,
  });
  // Only CUSTOMER partners can own a project. INTERNAL (integration/credentials)
  // partners are excluded from the owner picker.
  const partnerOptions = partners
    .filter((p) => p.partnerCategory === 'CUSTOMER')
    .map((p) => ({
      value: p._id,
      label: p.name || p._id,
    }));

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const { data: existing, isLoading: fetchLoading } = useQuery({
    queryKey: ['projects', id],
    queryFn: async () => await projectsService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  useBreadcrumbTitle(isEditMode ? existing?.name : null);

  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        name: existing.name || '',
        appCode: existing.appCode || '',
        description: existing.description || '',
        imageUrl: existing.imageUrl || '',
        partnerMasterId: existing.partnerMasterId || '',
        startAt: existing.startAt ? String(existing.startAt).split('T')[0] : '',
        paymentDone: existing.paymentDone ?? false,
      });
    }
  }, [existing, isEditMode, reset]);

  const createMut = useMutation({
    mutationFn: (payload: any) => projectsService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.current?.show({
        severity: 'success',
        summary: 'Project Created',
        detail: 'Project created successfully.',
        life: 3000,
      });
      // Return to the list after create (mirrors the update flow) instead of
      // dropping the user back on the edit form.
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to save Project'),
        life: 10000,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (payload: any) => projectsService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Project updated successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to save Project'),
        life: 10000,
      });
    },
  });

  const isSaving = createMut.isPending || updateMut.isPending;

  const onSubmit = (data: ProjectFormData) => {
    const payload: {
      name: string; appCode: string; description?: string; imageUrl: string | null;
      partnerMasterId: string | null; startAt: string | null; paymentDone: boolean;
    } = {
      name: data.name,
      appCode: data.appCode,
      // Logo/cover image URL (optional). Send null to clear it on edit.
      imageUrl: data.imageUrl?.trim() || null,
      // Owning partner (optional). Send null to clear; the BE stores it as an FK and it
      // only widens read-scope for callers with the partner-scope permission.
      partnerMasterId: data.partnerMasterId || null,
      // Project start date (optional). Send null to clear.
      startAt: data.startAt || null,
      paymentDone: !!data.paymentDone,
    };

    if (data.description && data.description.trim()) {
      payload.description = data.description;
    }

    if (isEditMode) updateMut.mutate(payload);
    else createMut.mutate(payload);
  };

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <PermissionGuard permission={PERMISSIONS.APP_READ}>
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-Project-Form">
        <ScreenHeader icon="pi pi-folder" title={isEditMode ? 'Edit Project' : 'Create Project'} subtitle={isEditMode ? 'Update project information and data source settings' : 'Add a new project'} onBack={() => handleNavigate('/projects')} />

        {/* TabView is always visible — Settings tab is locked until after first save */}
        <TabView className="project-form-tabs">

          {/* ── Tab 1: Project Details ── */}
          <TabPanel header="Project Details" leftIcon="pi pi-info-circle mr-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="ds-card p-4 mb-3">
                <h2 className="text-base font-semibold text-ink mb-3">
                  Project Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        value={field.value || ''}
                        label="Name"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        required
                        placeholder="Enter project name"
                      />
                    )}
                  />

                  <Controller
                    name="appCode"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        value={field.value || ''}
                        label="Project Code"
                        error={!!errors.appCode}
                        helperText={errors.appCode?.message}
                        required
                        placeholder="Enter project code (e.g. CRPC_APP)"
                      />
                    )}
                  />

                  <Controller
                    name="partnerMasterId"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormSelect
                        {...field}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        label="Partner"
                        options={partnerOptions}
                        placeholder="Select partner (optional)"
                        showClear
                        error={!!errors.partnerMasterId}
                        helperText={errors.partnerMasterId?.message || 'Owning partner — only affects partner-wide project access.'}
                      />
                    )}
                  />

                  <Controller
                    name="startAt"
                    control={control}
                    render={({ field }) => (
                      <FormDatePicker
                        {...field}
                        value={field.value || ''}
                        label="Start Date"
                        error={!!errors.startAt}
                        helperText={errors.startAt?.message || 'When the project starts (optional).'}
                      />
                    )}
                  />

                  <Controller
                    name="paymentDone"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Done</label>
                        <div className="flex items-center gap-2 mt-1">
                          <InputSwitch checked={!!value} onChange={(e: any) => onChange(e.value)} />
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {value ? 'Paid' : 'Not paid'}
                          </span>
                        </div>
                        <span className="text-xs text-ink-muted">
                          Not applicable for internal partners.
                        </span>
                      </div>
                    )}
                  />

                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        value={field.value || ''}
                        label="Description"
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        placeholder="Enter description (optional)"
                      />
                    )}
                  />

                  <Controller
                    name="imageUrl"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-start gap-3">
                        {field.value && /^(https?:\/\/|\/)/.test(field.value) && (
                          <img
                            src={field.value}
                            alt="Project logo preview"
                            className="w-11 h-11 rounded-lg object-cover border border-line flex-shrink-0 mt-6"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                            onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'visible'; }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <FormInput
                            {...field}
                            value={field.value || ''}
                            label="Image URL"
                            error={!!errors.imageUrl}
                            helperText={errors.imageUrl?.message || 'Logo/cover shown on the login project selector (optional).'}
                            placeholder="https://… (optional)"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  label="Cancel"
                  severity="secondary"
                  outlined
                  onClick={() => handleNavigate('/projects')}
                  disabled={isSaving}
                />
                <Button
                  type="submit"
                  label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create & Continue'}
                  icon={isSaving ? 'pi pi-spin pi-spinner' : isEditMode ? undefined : 'pi pi-arrow-right'}
                  iconPos="right"
                  disabled={isSaving}
                />
              </div>
            </form>
          </TabPanel>

          {/* ── Tab 2: Settings & Data Source ── */}
          <TabPanel
            header="Settings &amp; Data Source"
            leftIcon="pi pi-database mr-2"
            disabled={!isEditMode}
          >
            {isEditMode ? (
              <ProjectSettingsTab projectId={id!} toastRef={toast} />
            ) : (
              // Placeholder shown when the user somehow lands here before creating
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
                  <i className="pi pi-database text-blue-500" style={{ fontSize: '1.75rem' }} />
                </div>
                <h3 className="text-base font-semibold text-ink mb-2">
                  Settings available after project is created
                </h3>
                <p className="text-sm text-ink-muted max-w-sm">
                  Fill in the Project Details tab and click <strong>Create &amp; Continue</strong>.
                  You'll be taken directly here to configure the data source, channels, and governance settings.
                </p>
              </div>
            )}
          </TabPanel>
        </TabView>
      </div>

      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="ProjectForm.Dialog.Leave"
      />
    </PermissionGuard>
  );
};

export default ProjectForm;
